import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage, FormikProps } from "formik";
import { FormInitialValues } from "./DownloadDialogWrapper";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import { MdEmail } from "react-icons/md";
import ATMHTMLEditor from "src/components/UI/atoms/formFields/ATMHTMLEditor/ATMHTMLEditor";
import { EmployerCertificateUpdateListResponse } from "src/models/EmployerCertificateUpdate.model";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  useAddEmployerCertificateMutation,
  useLazyGetemployerCertificateDoneApplicationByIdQuery,
} from "src/services/EmployerCertificateService";
import { useLazyGetPanFormByPanIdQuery } from "src/services/PANService";
import { panFormToCertificate } from "src/pages/pan/view/Dialog/PanFormDialogBox/panFormToCertificate";
import { showToast } from "src/utils/toaster/showToast";
import { ackFileBaseName, fileExtensionFromUrl } from "src/utils/downloadFileAs";
import EmployerCertificateTemplate from "src/pages/employer-certificate/EmployerCertificateTemplate";
import {
  employerCertificateApplicant,
  employerCertificateDate,
} from "src/pages/employer-certificate/employerCertificateApplicant";
import { useGetPcoFirmsQuery } from "src/services/PcoFirmService";
import { PcoFirm, findPcoFirm } from "src/pages/employer-certificate/pcoFirms";
import { useEnsureDsaSignatureMutation } from "src/services/DsaIssuerService";
import {
  CertificateIssuer,
  dsaIssuerFor,
  isDsaCertificate,
  cleanDsaStamp,
  resolveDsaSignature,
} from "src/pages/employer-certificate/certificateIssuer";

type Props = {
  businessEnquiryData: EmployerCertificateUpdateListResponse | null;
  onClose: () => void;
};

const DownloadDialog = ({ businessEnquiryData, onClose }: Props) => {
  const [todayDate] = useState<string>(employerCertificateDate);

  // ---------------------------------------------------------------------------
  // Applicant block (name / father's name / residential address).
  //
  // These used to be typed into the Verify Application dialog and read straight
  // off the application. They now come from the PAN form the operator filled —
  // the reviewed source of truth, and the only place that holds the APPLICANT's
  // address rather than the retailer's. `acknowledgement_*` and the raw
  // application fields stay as fallbacks for applications with no PAN form.
  //
  // The employer block below (firm name, retailer / distributor name, PAN,
  // mobile, district) is untouched — it describes the employer, not the
  // applicant.
  // ---------------------------------------------------------------------------
  const [applicantName, setApplicantName] = useState<string>("");
  const [fatherName, setFatherName] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  /*
    Place. Same source chain the Affidavit dialog uses: the PAN form first,
    then the `acknowledgement_place` typed at login, and only then the
    retailer's / distributor's district. The district was the ONLY source
    before, which printed the counter's town on a certificate that names the
    applicant — on a DSA / PCO correction, where the form carries no residence
    block, that was always the wrong town.
  */
  const [place, setPlace] = useState<string>("");
  // Office Address as the PAN form holds it, and the applicant's cropped photo.
  const [officeAddress, setOfficeAddress] = useState<string>("");
  const [photoUrl, setPhotoUrl] = useState<string>("");
  /*
    PCO: the firm whose issuer details and seal & signature the certificate
    prints. Picked from the office address the PAN form printed (findPcoFirm);
    the operator can switch it in Edit. "" = not a PCO certificate / none.
  */
  const { data: pcoFirmsRes, isLoading: isPcoFirmsLoading } =
    useGetPcoFirmsQuery(undefined);
  const pcoFirms: PcoFirm[] = pcoFirmsRes?.data || [];
  const [pcoFirmId, setPcoFirmId] = useState<string>("");
  const [isPcoFirmPicked, setIsPcoFirmPicked] = useState(false);
  /*
    DSA: the agent who filed the application issues the certificate, with
    their seal and signature (generated from their profile name and saved the
    first time). Read off the single-application endpoint, since the list row
    is projected through the role's access fields and may not carry them.
  */
  const isDsa = isDsaCertificate(businessEnquiryData);
  const [dsaIssuer, setDsaIssuer] = useState<CertificateIssuer | null>(null);
  const [dsaIssuerRow, setDsaIssuerRow] = useState<any>(null);
  const [ensureDsaSignature] = useEnsureDsaSignatureMutation();
  const issuer = isDsa
    ? dsaIssuer
    : pcoFirms.find((firm) => firm._id === pcoFirmId) || null;
  const [isEditing, setIsEditing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [prefillSource, setPrefillSource] = useState<
    "LOADING" | "PAN_FORM" | "APPLICATION"
  >("LOADING");

  const [fetchPanForm] = useLazyGetPanFormByPanIdQuery();
  const [fetchApplication] =
    useLazyGetemployerCertificateDoneApplicationByIdQuery();

  /*
    The prefill is async and `isInitialized` is only set at its end, so a new
    `businessEnquiryData` reference arriving meanwhile (the list refetching)
    started a second run — which, for a DSA agent with no signature yet, also
    generated and saved the signature twice. One run per dialog.
  */
  const prefillStartedRef = useRef(false);

  useEffect(() => {
    if (!businessEnquiryData || isInitialized || prefillStartedRef.current) return;
    prefillStartedRef.current = true;

    const prefill = async () => {
      let fromForm: ReturnType<typeof panFormToCertificate> = {};
      const panId = businessEnquiryData?._id;
      if (panId) {
        try {
          const res: any = await fetchPanForm(panId);
          fromForm = panFormToCertificate(
            res?.data?.data?.formData,
            res?.data?.data?.applicationType
          );
        } catch {
          // A failed lookup is not fatal — fall through to the old sources.
          fromForm = {};
        }
      }

      const applicant = employerCertificateApplicant(
        businessEnquiryData,
        fromForm
      );
      setApplicantName(applicant.applicantName);
      setFatherName(applicant.fatherName);
      setAddress(applicant.address);
      setPlace(applicant.place);
      setOfficeAddress(applicant.officeAddress);

      /*
        The list row may not carry the photo fields (the admin list is
        projected through the role's access fields), so the application is
        read once more for them when they are missing.
      */
      let photo = applicant.photoUrl;
      let app: any = null;
      if ((!photo || isDsa) && panId) {
        try {
          const appRes: any = await fetchApplication(panId);
          app = appRes?.data?.data || null;
        } catch {
          app = null;
        }
      }
      if (!photo) photo = app?.editedPhotoUrl || app?.passportPhotoUrl || "";
      setPhotoUrl(photo);

      if (isDsa) {
        const source = app || businessEnquiryData;
        const signature = await resolveDsaSignature(source, (args) =>
          ensureDsaSignature(args)
        );
        if (signature.warning) showToast("error", signature.warning);
        // The agent's signature row plus the application's logo entry (logo,
        // firm name, seal), from the freshest read.
        setDsaIssuerRow({
          dsaIssuer: source?.dsaIssuer,
          dsaLogo: source?.dsaLogo,
          ...(source?.logos ? { logos: source.logos } : {}),
        });
        // Seal and signature printed without their white background.
        const stamp = await cleanDsaStamp(source, signature.signatureImage);
        setDsaIssuer(
          dsaIssuerFor(source, stamp.signatureImage, stamp.sealImage)
        );
      }

      setPrefillSource(
        Object.keys(fromForm).length ? "PAN_FORM" : "APPLICATION"
      );
      setIsInitialized(true);
    };

    prefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessEnquiryData, isInitialized]);

  // Once both the prefill (office address) and the firm list are in.
  useEffect(() => {
    if (!isInitialized || isPcoFirmsLoading || isPcoFirmPicked) return;
    // A DSA certificate is issued by the agent, never by a PCO firm.
    const firm = isDsa
      ? null
      : findPcoFirm(pcoFirms, officeAddress, businessEnquiryData);
    setPcoFirmId(firm?._id || "");
    setIsPcoFirmPicked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, isPcoFirmsLoading, isPcoFirmPicked, pcoFirmsRes]);

  const [addEmployerCertificate, addEmployerCertificateInfo] =
    useAddEmployerCertificateMutation();

  const generatePDF = () => {
    /*
      The applicant block is filled asynchronously (see the prefill effect
      above), and this function serialises whatever #pdfContent holds RIGHT
      NOW and posts it as the certificate. Called before the prefill lands,
      it renders a PDF with dotted rules where the name, father's name and
      residential address belong — the blank certificates reported from the
      field. The button is disabled while LOADING; this is the second lock,
      because the cost of getting it wrong is a blank legal document handed
      to an applicant.
    */
    if (prefillSource === "LOADING" || !isPcoFirmPicked) return;
    const input = document.getElementById("pdfContent") as HTMLElement;
    console.log("input", input, input?.outerHTML)
    addEmployerCertificate({
      html: input?.outerHTML,
      panId: businessEnquiryData?._id,
    }).then((res: any) => {
      if (res.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        console.log("res?.data", res?.data);
        if (
          res?.data &&
          res?.data?.data &&
          res?.data?.data?.employerertificateFile
        ) {
          downloadPdfFile(res?.data?.data?.employerertificateFile);

          console.log(res);
          showToast("success", res?.data?.message);
          onClose();
        }
      }
    });
  };

  const downloadPdfFile = async (pdfUrl: string) => {
    try {
      const response = await fetch(pdfUrl, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch the PDF file.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const segments = pdfUrl.split(/[\/?#]/);
      // `<ack>_Employer_Certificate.pdf` when the application has an acknowledgement
      // number, else the server's own file name.
      const ack = businessEnquiryData?.acknowledgementNumber;
      const fileName = ack
        ? `${ackFileBaseName(ack, "Employer_Certificate")}.${fileExtensionFromUrl(pdfUrl) || "pdf"}`
        : segments[segments.length - 1] || "downloaded-file.pdf";

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const editInputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 10px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "14px",
  };

  return (
    <>
      {/* Where the applicant details came from, and the panel to correct them.
          Both sit OUTSIDE #pdfContent, so neither reaches the generated PDF. */}
      {prefillSource !== "LOADING" && (
        <div
          style={{
            padding: "8px 20px",
            fontSize: "12.5px",
            borderBottom: "1px solid #ddd",
            background: prefillSource === "PAN_FORM" ? "#f1f8f1" : "#fdf6e3",
            color: "#333",
          }}
        >
          {prefillSource === "PAN_FORM"
            ? "Applicant details filled from the PAN form of this application. Use Edit to correct anything before downloading."
            : "No PAN form found for this application — applicant details come from the application record. Fill the PAN form first, or use Edit to type them."}
        </div>
      )}

      {/* The residence address is the one detail with no safe fallback: a DSA /
          PCO correction carries no residential block on its form, and the
          retailer's address (printed above as the Office Address) is not the
          applicant's. When neither the PAN form nor the login left one, say so
          rather than letting the certificate print a bare dotted rule. */}
      {prefillSource !== "LOADING" && !address.trim() && (
        <div
          style={{
            padding: "8px 20px",
            fontSize: "12.5px",
            borderBottom: "1px solid #ddd",
            background: "#fdecea",
            color: "#8a1c12",
            fontWeight: 500,
          }}
        >
          No residential address found on the PAN form or the application. Use
          Edit to type the applicant's residential address before downloading.
        </div>
      )}

      {isEditing && (
        <div
          style={{
            padding: "15px 20px",
            backgroundColor: "#f5f5f5",
            borderBottom: "1px solid #ddd",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <div style={{ fontWeight: 600, fontSize: "14px" }}>Edit Details</div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <label
                style={{ fontSize: "12px", fontWeight: 500, display: "block", marginBottom: "4px" }}
              >
                Applicant Name
              </label>
              <input
                type="text"
                value={applicantName}
                onChange={(e) => setApplicantName(e.target.value)}
                style={editInputStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <label
                style={{ fontSize: "12px", fontWeight: 500, display: "block", marginBottom: "4px" }}
              >
                Father's Name
              </label>
              <input
                type="text"
                value={fatherName}
                onChange={(e) => setFatherName(e.target.value)}
                style={editInputStyle}
              />
            </div>
          </div>
          <div>
            <label
              style={{ fontSize: "12px", fontWeight: 500, display: "block", marginBottom: "4px" }}
            >
              Residential Address
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              style={editInputStyle}
            />
          </div>
          <div style={{ maxWidth: "260px" }}>
            <label
              style={{ fontSize: "12px", fontWeight: 500, display: "block", marginBottom: "4px" }}
            >
              Place
            </label>
            <input
              type="text"
              value={place}
              onChange={(e) => setPlace(e.target.value)}
              style={editInputStyle}
            />
          </div>
          {!isDsa && pcoFirms.length > 0 && (
            <div style={{ maxWidth: "360px" }}>
              <label
                style={{ fontSize: "12px", fontWeight: 500, display: "block", marginBottom: "4px" }}
              >
                Issuing Firm (PCO)
              </label>
              <select
                value={pcoFirmId}
                onChange={(e) => setPcoFirmId(e.target.value)}
                style={editInputStyle}
              >
                <option value="">None — agent's details, no seal</option>
                {pcoFirms.map((firm) => (
                  <option key={firm._id} value={firm._id}>
                    {firm.firmName}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      <div>
      <EmployerCertificateTemplate
        data={
          isDsa && dsaIssuerRow
            ? { ...businessEnquiryData, ...dsaIssuerRow }
            : businessEnquiryData
        }
        applicantName={applicantName}
        fatherName={fatherName}
        address={address}
        place={place}
        todayDate={todayDate}
        officeAddress={officeAddress}
        photoUrl={photoUrl}
        issuer={issuer}
      />
    {/* <br /><br /><br />
        <div
          id="pdfContent"
          style={{
            width: "210mm",
            margin: "auto",
            padding: 40,
            marginBottom: "20px",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <img
              src={
                businessEnquiryData?.logos?.image
                  ? businessEnquiryData?.logos?.image
                  : ""
              }
              alt="Company Logo"
              style={{ width: "100%", height: "120px" }}
            />
            <p
              style={{
                margin: 0,
                marginTop: 10,
                fontWeight: 700,
                textTransform: "capitalize",
              }}
            >
              { businessEnquiryData?.retailerData?.address?'C/O '+businessEnquiryData?.retailerData?.firmName+', '+  businessEnquiryData?.retailerData?.address + ' ' + businessEnquiryData?.retailerData?.area+ ' ' + businessEnquiryData?.retailerData?.cityVillageName+ ' ' + businessEnquiryData?.retailerData?.district
              + ' ' + businessEnquiryData?.retailerData?.state+ ' ' + businessEnquiryData?.retailerData?.pincode: businessEnquiryData?.retailerFirmAddress
                ? businessEnquiryData?.retailerFirmAddress
                : "N/A"}
            </p>
          </div>
          <hr />
          <div
            style={{
              textAlign: "center",
              fontSize: 24,
              fontWeight: "bold",
              marginBottom: 2,
              marginTop: 20,
              textDecoration: "underline",
            }}
          >
            Annexure - B
          </div>
          <p style={{ marginBottom: 5, textAlign: "center", marginTop: 0 }}>
            (Certificate to be used by the Employer on the letterhead of the
            organization/institution under sub-rule (4) of rule 114 of the
            Income-tax Rules, 1962)
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ width: "60%" }}></div>
            <div style={{ width: "40%" }}>
              <div
                style={{
                  width: "60%",
                  borderStyle: "solid",
                  borderWidth: 2,
                  borderColor: "black",
                  margin: "30px 0px",
                }}
              >
                <p style={{ fontSize: 14, margin: 0, padding: 8 }}>
                  (Affix same photograph as affixed on PAN application form)
                </p>
              </div>
              <p style={{ fontSize: 14, marginBottom: 35 }}>
                (To be attested by issuing authority with his/her signature and
                rubber stamp appearing half on the photograph and half of the
                certificate)
              </p>
            </div>
          </div>
          <div style={{ fontSize: 14, lineHeight: "1.5" }}>
            <div style={{ marginBottom: 15 }}>
              <label style={{ fontWeight: "bold" }}>
                It is hereby certified that Sh./Smt./Kum:{" "}
              </label>
              <p style={{ textDecoration: "underline", display: "inline", minWidth:'150px' }}>
                {businessEnquiryData?.acknowledgement_name?businessEnquiryData?.acknowledgement_name:'N/A'}{" "}
              </p>
              <label style={{ marginLeft: 10 }}>Son/Daughter of:{" "}</label>

              <p style={{ textDecoration: "underline", display: "inline", minWidth:'150px' }}>
                {businessEnquiryData?.acknowledgement_father_name?businessEnquiryData?.acknowledgement_father_name:'N/A'}{" "}
              </p>
              <label>is employed with us since </label>

              <p style={{ textDecoration: "underline", display: "inline" }}>
                {businessEnquiryData?.randomNumber
                  ? businessEnquiryData?.randomNumber
                  : 2}
              </p>
              <label style={{ marginLeft: 10 }}>
                Years. He/She is presently working at the following address of
                the organization:
              </label>
            </div>
            <div style={{ marginBottom: 15 }}>
              <label style={{ fontWeight: "bold" }}>Office Address:</label>
              <input
                type="text"
                // placeholder="Enter Office Address"
                defaultValue= { businessEnquiryData?.retailerData?.address?'C/O '+businessEnquiryData?.retailerData?.firmName+', '+  businessEnquiryData?.retailerData?.address + ' ' + businessEnquiryData?.retailerData?.area+ ' ' + businessEnquiryData?.retailerData?.cityVillageName+ ' ' + businessEnquiryData?.retailerData?.district
                  + ' ' + businessEnquiryData?.retailerData?.state+ ' ' + businessEnquiryData?.retailerData?.pincode: businessEnquiryData?.retailerFirmAddress
                    ? businessEnquiryData?.retailerFirmAddress
                    : "N/A"}
                readOnly
                style={{
                  border: "none",
                  borderBottom: "1px solid black certificate-input",
                  width: "80%",
                  padding: 3,
                  textTransform: "capitalize",
                  textDecoration: "underline",
                }}
              />
            </div>
            <div style={{ marginBottom: 15 }}>
              <label style={{ fontWeight: "bold" }}>
                The residential address of the applicant as verified by us is
                given below:
              </label>
            </div>
            <div style={{ marginBottom: 15 }}>
              <label>Residential Address: </label>
              <input
                type="text"
                // placeholder="Enter Residential Address"
                defaultValue={businessEnquiryData?.acknowledgement_address?businessEnquiryData?.acknowledgement_address:''}
                readOnly
                style={{
                  border: "none",
                  borderBottom: "1px solid black certificate-input",
                  width: "80%",
                  padding: 3,
                  textTransform: "capitalize",
                  textDecoration: "underline",
                }}
              />
            </div>
            <div style={{ marginBottom: 15 }}>
              <label>
                Registration Number of the Company/Institution etc.:
              </label>
              <input
                type="text"
                // placeholder="Enter Registration Number"
                defaultValue={"N/A"}
                readOnly
                style={{
                  border: "none",
                  borderBottom: "1px solid black certificate-input",
                  width: 300,
                  padding: 3,
                }}
              />
            </div>
            <div style={{ marginBottom: 15 }}>
              <label>PAN of the Company/Institution:</label>
              <input
                type="text"
                // placeholder="Enter PAN"
                defaultValue={"N/A"}
                readOnly
                style={{
                  border: "none",
                  borderBottom: "1px solid black certificate-input",
                  width: 300,
                  padding: 3,
                }}
              />
            </div>
            <div style={{ fontWeight: "bold", marginBottom: 15 }}>
              <label>Details of the Individual issuing the certificate:</label>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ width: "60%" }}>
                <div style={{ marginBottom: 10 }}>
                  <label>Full Name: </label>
                  <input
                    type="text"
                    // placeholder="Enter Full Name"
                    defaultValue={ businessEnquiryData?.retailerData?.name?businessEnquiryData?.retailerData?.name: businessEnquiryData?.retailerName 
                        ? businessEnquiryData?.retailerName
                        : ""}
                    readOnly
                    style={{
                      border: "none",
                      borderBottom: "1px solid black certificate-input",
                      width: 300,
                      padding: 3,
                      textTransform: "capitalize",
                      textDecoration: "underline",
                    }}
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label>Designation:</label>
                  <input
                    type="text"
                    // placeholder="Enter Designation"
                    defaultValue={"Proprietor"}
                    readOnly
                    style={{
                      border: "none",
                      borderBottom: "1px solid black certificate-input",
                      width: 200,
                      padding: 3,
                    }}
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label>PAN of the Individual:</label>
                  <input
                    type="text"
                    // placeholder="Enter PAN"
                    defaultValue={

                      businessEnquiryData?.retailerData?.panNumber?businessEnquiryData?.retailerData?.panNumber: 
                      businessEnquiryData?.retailerPanNumber ? businessEnquiryData?.retailerPanNumber:''
                    
                    }
                    readOnly
                    style={{
                      border: "none",
                      borderBottom: "1px solid black certificate-input",
                      width: 300,
                      padding: 3,
                    }}
                  />
                </div>
                <div style={{ marginBottom: 10, display:'flex' }}>
                  <label style={{minWidth: '190px',lineHeight: 2}}>Office Address with Location: </label>
                  <input
                    type="text"
                    // placeholder="Enter Office Address"
                    defaultValue=  { businessEnquiryData?.retailerData?.district?businessEnquiryData?.retailerData?.district : "N/A"}
                    readOnly
                    style={{
                      border: "none",
                      borderBottom: "1px solid black certificate-input",
                      width: "80%",
                      padding: 3,
                      textTransform: "capitalize",
                      textDecoration: "underline",
                    }}
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label>Telephone: </label>
                  <input
                    type="text"
                    // placeholder="Enter Telephone Number"
                    defaultValue={"N/A"}
                    readOnly
                    style={{
                      border: "none",
                      borderBottom: "1px solid black certificate-input",
                      width: 300,
                      padding: 3,
                    }}
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label>Mobile: </label>
                  <input
                    type="text"
                    // placeholder="Enter Mobile Number"
                    defaultValue={
                      businessEnquiryData?.retailerData?.mobileNumber?businessEnquiryData?.retailerData?.mobileNumber: 

                      businessEnquiryData?.retailerMobileNumber
                        ? businessEnquiryData?.retailerMobileNumber
                        : "N/A"
                    }
                    readOnly
                    style={{
                      border: "none",
                      borderBottom: "1px solid black certificate-input",
                      width: 300,
                      padding: 3,
                      textDecoration: "underline",
                    }}
                  />
                </div>
                <div style={{ marginTop: 25 }}>
                  <div style={{ marginBottom: 10 }}>
                    <label>Date: </label>
                    <input
                      type="text"
                      // placeholder="Date"
                      defaultValue={todayDate}
                      readOnly
                      style={{
                        border: "none",
                        borderBottom: "1px solid black certificate-input",
                        width: 200,
                        padding: 3,
                      }}
                    />
                  </div>
                  <label>Place: </label>
                  <input
                    type="text"
                    placeholder="Place"
                    defaultValue={
                      businessEnquiryData?.retailerData?.district?businessEnquiryData?.retailerData?.district: 
                      businessEnquiryData?.retailerData?.district
                        ? businessEnquiryData?.retailerData?.district
                        : ""
                    }
                    readOnly
                    style={{
                      border: "none",
                      borderBottom: "1px solid black certificate-input",
                      width: 100,
                      padding: 3,
                      textTransform: "uppercase", // Ensures text is always uppercase
                    }}
                  />
                  <p />
                </div>
              </div>
              <div
                style={{
                  width: "40%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div>
                  <p>
                    (Signature) <br /> Office Seal
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div> */}
      </div>

      <div className="flex bottom-0 right-0 p-3  justify-end gap-3 absolute bg-white z-50 w-full">
        <ATMLoadingButton
          className="border-primary-main text-primary-main bg-white"
          onClick={onClose}
        >
          Close
        </ATMLoadingButton>

        {!isEditing ? (
          <ATMLoadingButton
            className="border-primary-main text-white bg-yellow-500"
            onClick={() => setIsEditing(true)}
          >
            <div className="font-medium flex items-center">
              <span>Edit</span>
            </div>
          </ATMLoadingButton>
        ) : (
          <ATMLoadingButton
            className="border-primary-main text-white bg-green-600"
            onClick={() => setIsEditing(false)}
          >
            <div className="font-medium flex items-center">
              <span>Save</span>
            </div>
          </ATMLoadingButton>
        )}

        <ATMLoadingButton
          type="submit"
          onClick={() => generatePDF()}
          className="bg-primary-main"
          loadingText="Sending..."
          isLoading={addEmployerCertificateInfo?.isLoading}
          disabled={
            prefillSource === "LOADING" ||
            !isPcoFirmPicked ||
            addEmployerCertificateInfo?.isLoading
          }
        >
          <div className=" font-medium  flex items-center">
            <span>Download</span>
          </div>
        </ATMLoadingButton>
      </div>
    </>
  );
};

export default DownloadDialog;
